import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Navbar from "../../Common/Navbar";
import "./Hosting.css";
import { connectRealtimeSocket } from "../CompleteCPanel/utils/realtimeSocket";

/* ==============================
   API BASE HELPER
============================== */
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiBase = (value) => {
  const clean = normalizeBase(value);
  if (!clean) return "http://localhost:5000/api";
  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiBase(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ==============================
   AUTH STORAGE KEYS
============================== */
const AUTH_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
  "isLoggedIn",
  "user",
  "currentUser",
  "email",
  "userEmail",
  "user_email",
];

/* ==============================
   FORM INITIAL STATE
============================== */
const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  domainName: "",
  hostingType: "agency_hosting",
  billingCycle: "monthly",
  planName: "Agency Hosting",
  planPrice: 350,
  message: "",
};

/* ==============================
   HOSTING PLANS
============================== */
const hostingPlans = [
  {
    key: "web_hosting",
    title: "Web Hosting",
    subtitle: "Perfect for beginners and small websites",
    price: 150,
    label: "Save up to 78%",
    badge: "Starter",
    features: [
      "Create 1 website",
      "10 GB SSD storage",
      "1 mailbox free for 1 year",
      "Free SSL certificate",
    ],
  },
  {
    key: "cloud_hosting",
    title: "Cloud Hosting",
    subtitle: "High performance hosting for growing brands",
    price: 220,
    label: "Save up to 70%",
    badge: "Fast",
    features: [
      "Create up to 3 websites",
      "20 GB SSD storage",
      "2 mailboxes free for 1 year",
      "Daily backup support",
    ],
  },
  {
    key: "agency_hosting",
    title: "Agency Hosting",
    subtitle: "Manage multiple client websites easily",
    price: 350,
    label: "Save up to 85%",
    badge: "Most Popular",
    features: [
      "Create up to 25 websites",
      "50 GB NVMe storage",
      "5 mailboxes free for 1 year",
      "Priority support",
    ],
  },
  {
    key: "node_hosting",
    title: "Node JS Hosting",
    subtitle: "Optimized for developers and backend apps",
    price: 550,
    label: "Save up to 55%",
    badge: "Developer",
    features: [
      "Node.js app deployment",
      "75 GB NVMe storage",
      "Git deployment support",
      "Advanced security",
    ],
  },
  {
    key: "pro_cloud_hosting",
    title: "Pro Cloud Hosting",
    subtitle: "Powerful cloud hosting for high traffic websites",
    price: 750,
    label: "Save up to 95%",
    badge: "Pro",
    features: [
      "Create up to 100 websites",
      "100 GB NVMe storage",
      "10 mailboxes free for 1 year",
      "24/7 expert support",
    ],
  },
];

/* ==============================
   AUTH HELPERS
============================== */
const getToken = () => {
  const tokenKeys = [
    "token",
    "authToken",
    "accessToken",
    "userToken",
    "jwtToken",
  ];

  for (const key of tokenKeys) {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }
  }

  return "";
};

const clearAuth = () => {
  AUTH_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  window.dispatchEvent(
    new CustomEvent("auth-state-changed", {
      detail: {
        source: "Hosting",
        action: "clear-auth",
      },
    })
  );
};

const getStoredUser = () => {
  const rawUser =
    localStorage.getItem("user") ||
    sessionStorage.getItem("user") ||
    localStorage.getItem("currentUser") ||
    sessionStorage.getItem("currentUser") ||
    "";

  if (!rawUser) return null;

  try {
    const parsed = JSON.parse(rawUser);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

/* ==============================
   PLAN HELPERS
============================== */
const normalizePlanKey = (value = "") => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");
};

const getPlanByKeyOrName = (value) => {
  const clean = normalizePlanKey(value);

  return (
    hostingPlans.find(
      (plan) =>
        normalizePlanKey(plan.key) === clean ||
        normalizePlanKey(plan.title) === clean
    ) || null
  );
};

const getBackendBillingCycle = (value) => {
  if (value === "3_months") return "quarterly";
  if (value === "12_months") return "yearly";
  return "monthly";
};

const getBillingCycleLabel = (value) => {
  if (value === "3_months") return "3 Months";
  if (value === "12_months") return "12 Months";
  return "1 Month";
};

const getBillingMultiplier = (value) => {
  if (value === "3_months") return 3;
  if (value === "12_months") return 12;
  return 1;
};

/* ==============================
   RAZORPAY SCRIPT LOADER
============================== */
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existingScript = document.querySelector(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(true), {
        once: true,
      });
      existingScript.addEventListener("error", () => resolve(false), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });
};

function Hosting() {
  const [formData, setFormData] = useState(initialForm);
  const [selectedPlanKey, setSelectedPlanKey] = useState("agency_hosting");
  const [activePlanKey, setActivePlanKey] = useState("");
  const [hostingFilter, setHostingFilter] = useState("web_hosting");
  const [subscriptionLength, setSubscriptionLength] = useState("1_month");
  const [loading, setLoading] = useState(false);
  const [paymentLoadingKey, setPaymentLoadingKey] = useState("");
  const [planLoading, setPlanLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [hostingAccount, setHostingAccount] = useState(null);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");

  const mountedRef = useRef(false);
  const planFetchInProgressRef = useRef(false);
  const planFetchedOnceRef = useRef(false);
  const planAbortControllerRef = useRef(null);

  const selectedPlan = useMemo(() => {
    return (
      hostingPlans.find((plan) => plan.key === selectedPlanKey) ||
      hostingPlans[2]
    );
  }, [selectedPlanKey]);

  const activePlan = useMemo(() => {
    return hostingPlans.find((plan) => plan.key === activePlanKey) || null;
  }, [activePlanKey]);

  const selectedPlanTotal = useMemo(() => {
    return selectedPlan.price * getBillingMultiplier(subscriptionLength);
  }, [selectedPlan, subscriptionLength]);

  /* ==============================
     AUTH FETCH
  ============================== */
  const authFetch = useCallback(async (endpoint, options = {}) => {
    const token = getToken();

    if (!token) {
      clearAuth();
      throw new Error("Session expired. Please login again.");
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));

    if (response.status === 401 || response.status === 403) {
      clearAuth();
      throw new Error("Session expired. Please login again.");
    }

    if (response.status === 429) {
      throw new Error("Too many requests. Please wait a moment and try again.");
    }

    if (!response.ok || data?.success === false) {
      throw new Error(data?.message || "Request failed.");
    }

    return data;
  }, []);

  /* ==============================
     APPLY PLAN TO FORM
     No dependency on subscriptionLength to avoid repeated /hosting/plan calls
  ============================== */
  const applyPlanToForm = useCallback((plan, billingValue = "1_month") => {
    if (!plan) return;

    setSelectedPlanKey(plan.key);

    setFormData((prev) => ({
      ...prev,
      hostingType: plan.key,
      billingCycle: getBackendBillingCycle(billingValue),
      planName: plan.title,
      planPrice: plan.price,
      message:
        prev.message ||
        `I want to choose ${plan.title} hosting plan at ₹${plan.price}/month.`,
    }));
  }, []);

  /* ==============================
     LOAD ACTIVE PLAN
     Runs once on page load or manually after payment
  ============================== */
  const loadCurrentPlan = useCallback(
    async ({ force = false } = {}) => {
      if (planFetchInProgressRef.current) return;
      if (planFetchedOnceRef.current && !force) return;

      planFetchInProgressRef.current = true;
      planFetchedOnceRef.current = true;

      if (planAbortControllerRef.current) {
        planAbortControllerRef.current.abort();
      }

      planAbortControllerRef.current = new AbortController();

      try {
        if (mountedRef.current) {
          setPlanLoading(true);
        }

        const data = await authFetch("/hosting/plan", {
          signal: planAbortControllerRef.current.signal,
        });

        const planData = data?.data || null;

        if (!planData) {
          if (mountedRef.current) {
            setActivePlanKey("");
          }
          return;
        }

        const matchedPlan =
          getPlanByKeyOrName(planData.planKey) ||
          getPlanByKeyOrName(planData.planName) ||
          getPlanByKeyOrName(planData.plan);

        if (matchedPlan && mountedRef.current) {
          setActivePlanKey(matchedPlan.key);
          applyPlanToForm(matchedPlan, subscriptionLength);
        }
      } catch (error) {
        if (error?.name === "AbortError") return;

        if (mountedRef.current) {
          setStatus({
            type: "error",
            message: error.message || "Failed to load active hosting plan.",
          });
        }
      } finally {
        planFetchInProgressRef.current = false;

        if (mountedRef.current) {
          setPlanLoading(false);
        }
      }
    },
    [authFetch, applyPlanToForm, subscriptionLength]
  );

  useEffect(() => {
    mountedRef.current = true;

    loadCurrentPlan({ force: false });

    return () => {
      mountedRef.current = false;

      if (planAbortControllerRef.current) {
        planAbortControllerRef.current.abort();
      }
    };
  }, [loadCurrentPlan]);

  /* ==============================
     REALTIME SOCKET
     Do not disconnect shared socket on unmount
  ============================== */
  useEffect(() => {
    const token = getToken();

    if (!token) {
      setRealtimeStatus("offline");
      return undefined;
    }

    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    const handleConnected = () => {
      setRealtimeStatus("connected");
    };

    const handleDisconnected = () => {
      setRealtimeStatus("offline");
    };

    const handleHostingStatus = (payload = {}) => {
      setRealtimeStatus("connected");

      if (payload?.type === "HOSTING_PLAN_SELECTED") {
        const matchedPlan =
          getPlanByKeyOrName(payload.planKey) ||
          getPlanByKeyOrName(payload.planName);

        if (matchedPlan) {
          setActivePlanKey(matchedPlan.key);
          applyPlanToForm(matchedPlan, subscriptionLength);
        }

        setStatus({
          type: "success",
          message: payload.message || "Hosting plan activated successfully.",
        });
      }

      if (payload?.type === "HOSTING_REQUEST_CREATED") {
        setStatus({
          type: "success",
          message: payload.message || "Hosting request submitted successfully.",
        });
      }
    };

    const handlePaymentStatus = (payload = {}) => {
      setRealtimeStatus("connected");

      if (payload?.status === "success") {
        setStatus({
          type: "success",
          message: payload.message || "Payment completed successfully.",
        });
      }
    };

    const handleSocketAuthError = () => {
      clearAuth();
      window.location.href = "/signin";
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);
    socket.on("hosting:status", handleHostingStatus);
    socket.on("payment:status", handlePaymentStatus);
    socket.on("connect_error", handleSocketAuthError);

    if (socket.connected) {
      handleConnected();
    }

    return () => {
      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);
      socket.off("hosting:status", handleHostingStatus);
      socket.off("payment:status", handlePaymentStatus);
      socket.off("connect_error", handleSocketAuthError);
    };
  }, [applyPlanToForm, subscriptionLength]);

  /* ==============================
     FORM CHANGE
  ============================== */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  /* ==============================
     ACTIVATE HOSTING AFTER PAYMENT
  ============================== */
  const activateHostingAfterPayment = useCallback(
    async ({ plan, verifiedPayment }) => {
      const paymentId =
        verifiedPayment?.payment?.paymentId ||
        verifiedPayment?.payment?.payment_id ||
        verifiedPayment?.paymentId ||
        "";

      const orderId =
        verifiedPayment?.payment?.orderId ||
        verifiedPayment?.payment?.order_id ||
        verifiedPayment?.orderId ||
        "";

      const data = await authFetch("/hosting/select-plan", {
        method: "POST",
        body: JSON.stringify({
          planKey: plan.key,
          planName: plan.title,
          price: plan.price,
          billingCycle: getBackendBillingCycle(subscriptionLength),
          paidAmount: plan.price * getBillingMultiplier(subscriptionLength),
          paymentId,
          orderId,
          paymentMode: verifiedPayment?.payment?.mode || "test",
        }),
      });

      if (mountedRef.current) {
        setActivePlanKey(plan.key);
      }

      return data;
    },
    [authFetch, subscriptionLength]
  );

  /* ==============================
     CHOOSE PLAN WITH RAZORPAY FLOW
  ============================== */
  const handleChoosePlan = useCallback(
    async (plan) => {
      try {
        setLoading(true);
        setPaymentLoadingKey(plan.key);
        setStatus({ type: "", message: "" });
        setHostingAccount(null);

        const token = getToken();

        if (!token) {
          clearAuth();
          setStatus({
            type: "error",
            message: "Session expired. Please login again.",
          });
          window.location.href = "/signin";
          return;
        }

        if (activePlanKey === plan.key) {
          applyPlanToForm(plan, subscriptionLength);
          setStatus({
            type: "success",
            message: `${plan.title} is already active on your account.`,
          });
          return;
        }

        applyPlanToForm(plan, subscriptionLength);

        const scriptLoaded = await loadRazorpayScript();

        if (!scriptLoaded) {
          throw new Error("Payment gateway failed to load. Please try again.");
        }

        const billingCycleLabel = getBillingCycleLabel(subscriptionLength);
        const billingMultiplier = getBillingMultiplier(subscriptionLength);
        const payableAmount = plan.price * billingMultiplier;

        const productPayload = {
          productType: "hosting",
          productName: plan.title,
          planKey: plan.key,
          planName: plan.title,
          billingCycle: getBackendBillingCycle(subscriptionLength),
          billingCycleLabel,
          amount: payableAmount,
        };

        const orderData = await authFetch("/payments/create-order", {
          method: "POST",
          body: JSON.stringify(productPayload),
        });

        if (!orderData?.order?.id || !orderData?.key) {
          throw new Error("Invalid payment order response.");
        }

        const storedUser = getStoredUser();

        const razorpayOptions = {
          key: orderData.key,
          amount: orderData.order.amount,
          currency: orderData.order.currency || "INR",
          name: "DoHostingPay",
          description: `${plan.title} - ${billingCycleLabel}`,
          order_id: orderData.order.id,

          prefill: {
            name:
              storedUser?.fullName ||
              storedUser?.name ||
              formData.fullName ||
              "",
            email: storedUser?.email || formData.email || "",
            contact: storedUser?.phone || formData.phone || "",
          },

          notes: {
            productType: "hosting",
            planKey: plan.key,
            billingCycle: getBackendBillingCycle(subscriptionLength),
          },

          theme: {
            color: "#6d28d9",
          },

          modal: {
            confirm_close: true,
            ondismiss: () => {
              setLoading(false);
              setPaymentLoadingKey("");
              setStatus({
                type: "error",
                message: "Payment popup closed. Your plan was not activated.",
              });
            },
          },

          handler: async (response) => {
            try {
              setStatus({
                type: "success",
                message: "Payment received. Verifying securely...",
              });

              const verifiedPayment = await authFetch("/payments/verify", {
                method: "POST",
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  ...productPayload,
                }),
              });

              await activateHostingAfterPayment({
                plan,
                verifiedPayment,
              });

              setStatus({
                type: "success",
                message: `${plan.title} payment successful and hosting plan activated.`,
              });

              document.querySelector("#hosting-request-form")?.scrollIntoView({
                behavior: "smooth",
              });

              await loadCurrentPlan({ force: true });
            } catch (error) {
              setStatus({
                type: "error",
                message:
                  error.message ||
                  "Payment verification failed or hosting activation failed.",
              });
            } finally {
              setLoading(false);
              setPaymentLoadingKey("");
            }
          },
        };

        const razorpay = new window.Razorpay(razorpayOptions);

        razorpay.on("payment.failed", () => {
          setLoading(false);
          setPaymentLoadingKey("");
          setStatus({
            type: "error",
            message: "Payment failed. Please try again.",
          });
        });

        razorpay.open();
      } catch (error) {
        setStatus({
          type: "error",
          message: error.message || "Unable to start payment.",
        });
        setLoading(false);
        setPaymentLoadingKey("");
      }
    },
    [
      activePlanKey,
      activateHostingAfterPayment,
      applyPlanToForm,
      authFetch,
      formData.email,
      formData.fullName,
      formData.phone,
      loadCurrentPlan,
      subscriptionLength,
    ]
  );

  /* ==============================
     HOSTING REQUEST FORM SUBMIT
  ============================== */
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      try {
        setLoading(true);
        setStatus({ type: "", message: "" });

        const token = getToken();

        if (!token) {
          clearAuth();
          setStatus({
            type: "error",
            message: "Session expired. Please login again.",
          });
          window.location.href = "/signin";
          return;
        }

        const data = await authFetch("/hosting/request", {
          method: "POST",
          body: JSON.stringify({
            ...formData,
            hostingType: selectedPlan.key,
            billingCycle: getBackendBillingCycle(subscriptionLength),
            planName: selectedPlan.title,
            planPrice: selectedPlan.price,
            totalAmount: selectedPlanTotal,
            subscriptionLength,
            hostingFilter,
          }),
        });

        setStatus({
          type: "success",
          message: data?.message || "Hosting request submitted successfully.",
        });

        setHostingAccount(data.account || null);
        setFormData({
          ...initialForm,
          hostingType: selectedPlan.key,
          billingCycle: getBackendBillingCycle(subscriptionLength),
          planName: selectedPlan.title,
          planPrice: selectedPlan.price,
        });
      } catch (err) {
        setStatus({
          type: "error",
          message: err.message || "Hosting request failed.",
        });
      } finally {
        setLoading(false);
      }
    },
    [
      authFetch,
      formData,
      hostingFilter,
      selectedPlan,
      selectedPlanTotal,
      subscriptionLength,
    ]
  );

  return (
    <>
      <Navbar />

      <section className="hosting-pricing-page">
        <div className="hosting-container">
          <div className="hosting-top-content">
            <span className="hosting-eyebrow">DoHostingPay India</span>

            <h1>Web Hosting Plans</h1>

            <p>
              Choose fast, secure and affordable hosting plans for websites,
              agencies, cloud projects and Node.js applications.
            </p>

            <div className="hosting-realtime-chip">
              <span
                className={
                  realtimeStatus === "connected"
                    ? "hosting-realtime-dot online"
                    : "hosting-realtime-dot offline"
                }
              />

              <span>
                {realtimeStatus === "connected"
                  ? "Realtime connected"
                  : realtimeStatus === "connecting"
                  ? "Connecting realtime..."
                  : "Realtime offline"}
              </span>
            </div>

            {activePlan ? (
              <div className="hosting-active-summary">
                <span>Active Plan</span>
                <strong>
                  {activePlan.title} — ₹{activePlan.price}/mo
                </strong>
              </div>
            ) : null}
          </div>

          <div className="hosting-filter-row">
            <select
              value={hostingFilter}
              onChange={(e) => setHostingFilter(e.target.value)}
            >
              <option value="web_hosting">Web Hosting</option>
              <option value="wordpress_hosting">WordPress Hosting</option>
              <option value="cloud_hosting">Cloud Hosting</option>
              <option value="agency_hosting">Agency Hosting</option>
              <option value="node_hosting">Node.js Hosting</option>
            </select>

            <select
              value={subscriptionLength}
              onChange={(e) => {
                const value = e.target.value;

                setSubscriptionLength(value);

                setFormData((prev) => ({
                  ...prev,
                  billingCycle: getBackendBillingCycle(value),
                }));
              }}
            >
              <option value="1_month">1 Month</option>
              <option value="3_months">3 Months</option>
              <option value="12_months">12 Months</option>
            </select>
          </div>

          <div className="hosting-trust-row">
            <span>🛡️ 30-day money-back guarantee</span>
            <span>🎧 24/7 support</span>
            <span>↻ Cancel anytime</span>
          </div>

          {planLoading ? (
            <div className="hosting-alert success">Loading active plan...</div>
          ) : null}

          {status.message ? (
            <div className={`hosting-alert ${status.type}`}>
              {status.message}
            </div>
          ) : null}

          <div className="marketplace-product-section__items-wrapper">
            {hostingPlans.map((plan) => {
              const isSelected = selectedPlanKey === plan.key;
              const isActive = activePlanKey === plan.key;
              const planTotal =
                plan.price * getBillingMultiplier(subscriptionLength);
              const isPlanProcessing = paymentLoadingKey === plan.key;

              return (
                <div
                  key={plan.key}
                  className={`marketplace-product-item ${
                    isSelected ? "active-card" : ""
                  } ${isActive ? "hosting-active-plan-card" : ""}`}
                >
                  <div className="marketplace-product-item__top-label">
                    {isActive ? "Currently Active" : plan.label}
                  </div>

                  <div className="marketplace-product-item__badge">
                    {isActive ? "Active Plan" : plan.badge}
                  </div>

                  <div className="marketplace-product-item__content">
                    <h3>{plan.title}</h3>

                    <p>{plan.subtitle}</p>

                    <div className="hosting-price-box">
                      <span className="hosting-currency">₹</span>
                      <strong>{plan.price}</strong>
                      <span>/mo</span>
                    </div>

                    {subscriptionLength !== "1_month" ? (
                      <p className="hosting-plan-total">
                        Total: ₹{planTotal} /{" "}
                        {getBillingCycleLabel(subscriptionLength)}
                      </p>
                    ) : null}

                    <button
                      type="button"
                      className={`marketplace-product-item__action-button ${
                        isActive ? "hosting-selected-button" : ""
                      }`}
                      onClick={() => handleChoosePlan(plan)}
                      disabled={loading || isActive}
                    >
                      {isActive
                        ? "✓ Active Plan"
                        : isPlanProcessing
                        ? "Processing..."
                        : isSelected
                        ? "Pay & Activate"
                        : "Choose Plan"}
                    </button>

                    <ul className="hosting-feature-list">
                      {plan.features.map((feature, index) => (
                        <li
                          key={index}
                          className={isActive ? "hosting-active-feature" : ""}
                        >
                          <span>✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="hosting-request-section" id="hosting-request-form">
        <div className="hosting-container">
          <div className="hosting-form-heading">
            <span>Selected Plan</span>

            <h2>
              {selectedPlan.title} - ₹{selectedPlan.price}/mo
            </h2>

            <p>
              Selected billing: {getBillingCycleLabel(subscriptionLength)}. Total
              payable amount: ₹{selectedPlanTotal}.
            </p>

            <p>
              Submit your details and our system will activate your hosting
              request.
            </p>
          </div>

          <form className="hosting-form" onSubmit={handleSubmit}>
            <input
              name="fullName"
              placeholder="Full Name"
              value={formData.fullName}
              onChange={handleChange}
              autoComplete="name"
            />

            <input
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
            />

            <input
              name="phone"
              placeholder="Phone"
              value={formData.phone}
              onChange={handleChange}
              autoComplete="tel"
            />

            <input
              name="domainName"
              placeholder="Domain Name"
              value={formData.domainName}
              onChange={handleChange}
              autoComplete="off"
            />

            <textarea
              name="message"
              placeholder="Message"
              value={formData.message}
              onChange={handleChange}
            />

            <button disabled={loading}>
              {loading ? "Submitting..." : "Submit Hosting Request"}
            </button>
          </form>

          {hostingAccount && (
            <div className="hosting-dashboard-box">
              <h2>🎉 Your Hosting is Ready!</h2>

              <div className="hosting-credentials">
                <p>
                  <strong>Domain:</strong> {hostingAccount.domain}
                </p>

                <p>
                  <strong>Username:</strong> {hostingAccount.username}
                </p>
              </div>

              <a
                href={hostingAccount.loginUrl}
                target="_blank"
                rel="noreferrer"
                className="hosting-dashboard-btn"
              >
                Go to Dashboard
              </a>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default Hosting;